import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Input,
  Spin,
  message,
  Select,
} from "antd";
import { Button, ArrowLeftIcon, PlusIcon } from "evergreen-ui";
import { useObjectVal } from "../Firebase/databaseHooks";
import Firebase from "../Firebase/Firebase";
import FireFetch from "../Firebase/FireFetch";
import { getDatabase, ref, update } from "firebase/database";
import { getCallable } from "../Firebase/functionsClient";

const db = getDatabase(Firebase);
const { TextArea } = Input;
const { Option } = Select;
const listPermissionsFn = getCallable("listPermissions");

const normalizePermissions = (rawPermissions) => {
  if (!rawPermissions) return [];

  if (Array.isArray(rawPermissions)) {
    return rawPermissions.filter(Boolean);
  }

  if (typeof rawPermissions === "object") {
    return Object.keys(rawPermissions).filter((key) => !!rawPermissions[key]);
  }

  if (typeof rawPermissions === "string") {
    return rawPermissions
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
  }

  return [];
};

const RoleDetails = ({ roleId, onBack, canUpdate = true }) => {
  const roleRef = useMemo(() => ref(db, `talmidsdb/userRoles/${roleId}`), [roleId]);

  const [role, loading, error] = useObjectVal(roleRef);
  const [allPerms, setAllPerms] = useState([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsError, setPermsError] = useState(null);

  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setPermissions(normalizePermissions(role.permissions));
    }
  }, [role]);

  useEffect(() => {
    let mounted = true;

    const loadPermissions = async () => {
      setPermsLoading(true);
      setPermsError(null);

      try {
        const res = await listPermissionsFn({});
        const permissionsList = Array.isArray(res?.data?.permissions) ? res.data.permissions : [];
        if (mounted) {
          setAllPerms(permissionsList);
        }
      } catch (err) {
        if (mounted) {
          setAllPerms([]);
          setPermsError(err);
        }
      } finally {
        if (mounted) {
          setPermsLoading(false);
        }
      }
    };

    loadPermissions();
    return () => {
      mounted = false;
    };
  }, []);

  const getPermMeta = (idOrCode) => {
    if (!allPerms) return undefined;
    return (
      allPerms.find((p) => p.id === idOrCode || p.code === idOrCode) || undefined
    );
  };

  const getPermLabel = (idOrCode) => {
    const meta = getPermMeta(idOrCode);
    if (!meta) return idOrCode;
    return meta.name || meta.description || meta.code || idOrCode;
  };

  const activeAvailablePerms = (allPerms || []).filter((p) => p.active !== false);

  const unassignedPermOptions = activeAvailablePerms.filter((p) => {
    return !permissions.includes(p.id) && !permissions.includes(p.code);
  });

  const handleAddPermission = async () => {
    if (!canUpdate) {
      message.error("You do not have permission to update role permissions.");
      return;
    }

    if (!selectedPermissions.length) {
      message.warning("Please select at least one permission to add.");
      return;
    }

    // Merge selected permissions into existing ones and dedupe
    const merged = Array.from(
      new Set([...permissions, ...selectedPermissions])
    );

    try {
      setSaving(true);
      await update(roleRef, {
        permissions: merged,
      });

      setPermissions(merged);
      setSelectedPermissions([]);

      // Log each added perm (optional)
      selectedPermissions.forEach((permId) => {
        FireFetch.addLog("userRoles", roleId, `Add permission: ${permId}`);
      });

      message.success("Permissions added.");
    } catch (e) {
      console.error("Failed to add permissions:", e);
      message.error("Failed to add permissions. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePermission = async (perm) => {
    if (!canUpdate) {
      message.error("You do not have permission to update role permissions.");
      return;
    }

    const updatedPermissions = permissions.filter((p) => p !== perm);

    try {
      setSaving(true);
      await update(roleRef, {
        permissions: updatedPermissions,
      });

      setPermissions(updatedPermissions);

      FireFetch.addLog("userRoles", roleId, `Remove permission: ${perm}`);

      message.success("Permission removed.");
    } catch (e) {
      console.error("Failed to remove permission:", e);
      message.error("Failed to remove permission. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || permsLoading) {
    return (
      <div
        style={{
          padding: 24,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Spin tip="Loading role details..." />
      </div>
    );
  }

  if (error || permsError) {
    return (
      <div style={{ padding: 24 }}>
        <p>
          Error loading role or permissions:{" "}
          {String(error || permsError || "Unknown error")}
        </p>
        <Button
          appearance="primary"
          iconBefore={ArrowLeftIcon}
          onClick={onBack}
        >
          Back
        </Button>
      </div>
    );
  }

  if (!role) {
    return (
      <div style={{ padding: 24 }}>
        <p>Role not found.</p>
        <Button
          appearance="primary"
          iconBefore={ArrowLeftIcon}
          onClick={onBack}
        >
          Back
        </Button>
      </div>
    );
  }

  const roleName = role.roleName || "Unknown role";
  const description = role.description || "No description provided.";

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
        <Button
          appearance="minimal"
          iconBefore={ArrowLeftIcon}
          onClick={onBack}
        >
          Back to Roles
        </Button>
      </div>

      <Card
        title={`Role Details: ${roleName}`}
        bordered
        style={{ maxWidth: 900, margin: "0 auto" }}
      >
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Role Name">
            {roleName}
          </Descriptions.Item>
          <Descriptions.Item label="Role ID">
            {role.id || roleId}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            <TextArea
              value={description}
              autoSize={{ minRows: 2, maxRows: 4 }}
              readOnly
            />
          </Descriptions.Item>
        </Descriptions>

        {/* PERMISSIONS SECTION */}
        <div style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 8 }}>Permissions</h3>
          <p style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
            Add or remove permissions for this role
          </p>

          {/* Existing permissions */}
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            {permissions.length === 0 && (
              <span style={{ color: "#999" }}>No permissions assigned yet.</span>
            )}

            {permissions.map((permId) => (
              <Tag
                key={permId}
                closable={canUpdate}
                onClose={(e) => {
                  e.preventDefault();
                  handleRemovePermission(permId);
                }}
                color="blue"
              >
                {getPermLabel(permId)}
              </Tag>
            ))}
          </div>

          {/* Add permission form */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Select permission(s) to add"
              value={selectedPermissions}
              onChange={(values) => setSelectedPermissions(values)}
              style={{ minWidth: 260, maxWidth: 400 }}
              disabled={!canUpdate || saving || unassignedPermOptions.length === 0}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {unassignedPermOptions.map((perm) => (
                <Option key={perm.id} value={perm.id}>
                  {perm.name || perm.code}{" "}
                  {perm.description ? ` - ${perm.description}` : ""}
                </Option>
              ))}
            </Select>

            <Button
              appearance="primary"
              iconBefore={PlusIcon}
              onClick={handleAddPermission}
              isLoading={saving}
              disabled={!canUpdate || !unassignedPermOptions.length}
            >
              Add Permission
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RoleDetails;
